/**
 * MN custom name/color: metafield-gated toggles + optional fee catalog lines.
 * Custom name: custom.custom_name_fee_variant → fee SKU id + price.
 * Custom color: checkbox → Color Element Group swatches → fee from selected group.
 */
if (!customElements.get('mn-product-custom-options')) {
  customElements.define(
    'mn-product-custom-options',
    class MnProductCustomOptions extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.productInfo = this.closest('product-info');
        this.priceUpdateTimer = null;
        /** @type {Map<string, {hex:string, feeCents:number, feeLabel:string, feeVariantId:number, title:string, name:string}>} */
        this.colorSelections = new Map();

        this.onColorToggle = this.onColorToggle.bind(this);
        this.onDefaultColorClick = this.onDefaultColorClick.bind(this);
        this.onNameToggle = this.onNameToggle.bind(this);
        this.onNameInput = this.onNameInput.bind(this);
        this.onOptionValueChange = this.onOptionValueChange.bind(this);
        this.onVariantUpdated = this.onVariantUpdated.bind(this);
        this.onHostChange = this.onHostChange.bind(this);
        this.onHostClick = this.onHostClick.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        this.onQuantityChange = this.onQuantityChange.bind(this);

        // Attach listeners first so the toggle still works if hydration throws.
        this.addEventListener('change', this.onHostChange);
        this.addEventListener('click', this.onHostClick);
        this.addEventListener('input', this.onHostChange);
        window.addEventListener('pageshow', this.onPageShow);

        try {
          this.config = this.readConfig();
        } catch (e) {
          console.error('[mn-custom-options] readConfig', e);
          this.config = {
            allowCustomColor: this.dataset.allowCustomColor === 'true',
            allowCustomName: this.dataset.allowCustomName === 'true',
            customNamePrice: Number(this.dataset.customNamePrice) || 0,
            customNameFeeVariantId: Number(this.dataset.customNameFeeVariantId) || 0,
            colorGroups: [],
            colorFeeVariants: [],
            nameFeeVariants: [],
            wallPreview: this.dataset.wallPreview === 'true',
            wallDefaultC1: this.dataset.wallDefaultC1 || '#FFFFFF',
            wallDefaultC2: this.dataset.wallDefaultC2 || '#FFFFFF',
            samplePurchase: this.dataset.samplePurchase === 'true',
            basePrice: Number(this.dataset.basePrice) || 0,
          };
        }

        this.refreshElements();
        this.variantPriceCents =
          Number(this.config.basePrice) ||
          Number(this.dataset.basePrice) ||
          this.readVariantPriceCents();

        try {
          this.hydrateColorGroupsFromConfig();
        } catch (e) {
          console.error('[mn-custom-options] hydrate', e);
        }

        this.bindColorFieldset();
        this.bindQuantityListener();

        this.optionChangeUnsubscriber = subscribe?.(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.onOptionValueChange
        );
        this.variantUpdateUnsubscriber = subscribe?.(PUB_SUB_EVENTS.variantChange, this.onVariantUpdated);

        this.resetToDefaults();
      }

      disconnectedCallback() {
        this.optionChangeUnsubscriber?.();
        this.variantUpdateUnsubscriber?.();
        this.unbindColorFieldset();
        this.unbindQuantityListener();
        this.removeEventListener('change', this.onHostChange);
        this.removeEventListener('click', this.onHostClick);
        this.removeEventListener('input', this.onHostChange);
        window.removeEventListener('pageshow', this.onPageShow);
        if (this.priceUpdateTimer) clearTimeout(this.priceUpdateTimer);
      }

      onPageShow(event) {
        const run = () => this.resetToDefaults();
        if (event.persisted) {
          run();
          return;
        }
        requestAnimationFrame(run);
      }

      resetToDefaults() {
        this.refreshElements();
        this.bindColorFieldset();
        this.clearError();

        this.colorSelections = new Map();

        if (this.colorToggle) this.colorToggle.checked = false;
        if (this.nameToggle) this.nameToggle.checked = false;
        if (this.nameInput) {
          this.nameInput.value = '';
          this.nameInput.hidden = true;
          this.nameInput.disabled = true;
        }

        this.clearSwatchSelectionUi();
        this.syncFromSelection();
        this.syncFeeHints();

        if (this.isSamplePurchase()) {
          const sampleBase =
            Number(this.config?.basePrice) || Number(this.dataset.basePrice) || 0;
          if (sampleBase > 0) this.variantPriceCents = sampleBase;
        } else {
          const price = this.readVariantPriceCents();
          if (price > 0) this.variantPriceCents = price;
        }
        this.updatePriceDisplay();
      }

      bindQuantityListener() {
        this.unbindQuantityListener();
        const input = this.getQuantityInput();
        if (!input) return;
        input.addEventListener('change', this.onQuantityChange);
        input.addEventListener('input', this.onQuantityChange);
        this.quantityInputEl = input;
      }

      unbindQuantityListener() {
        if (!this.quantityInputEl) return;
        this.quantityInputEl.removeEventListener('change', this.onQuantityChange);
        this.quantityInputEl.removeEventListener('input', this.onQuantityChange);
        this.quantityInputEl = null;
      }

      onQuantityChange() {
        this.updatePriceDisplay();
      }

      getQuantityInput() {
        return (
          this.productInfo?.querySelector('input.quantity__input[name="quantity"]') ||
          document.getElementById(`Quantity-${this.sectionId}`) ||
          document.querySelector(`#Quantity-Form-${this.sectionId} input.quantity__input`)
        );
      }

      getSelectedQuantity() {
        const n = parseInt(this.getQuantityInput()?.value, 10);
        return Number.isFinite(n) && n > 0 ? n : 1;
      }

      refreshElements() {
        this.colorWrap = this.querySelector('[data-mn-custom-color-wrap]');
        this.colorToggle = this.querySelector('[data-mn-custom-color-toggle]');
        this.colorPanel = this.querySelector('[data-mn-custom-color-panel]');
        this.colorProperty = this.querySelector('[data-mn-custom-color-property]');
        this.colorDataProperty = this.querySelector('[data-mn-custom-color-data-property]');
        this.colorFeeProperty = this.querySelector('[data-mn-custom-color-fee-property]');
        this.colorFeeCents = this.querySelector('[data-mn-custom-color-fee-cents]');
        this.colorFeeHint = this.querySelector('[data-mn-custom-color-fee-hint]');
        this.colorError = this.querySelector('[data-mn-custom-color-error]');
        this.colorSwatches = this.querySelectorAll('[data-mn-color-swatch]');
        this.nameToggle = this.querySelector('[data-mn-custom-name-toggle]');
        this.nameInput = this.querySelector('[data-mn-custom-name-input]');
        this.nameFeeProperty = this.querySelector('[data-mn-custom-name-fee-property]');
        this.nameFeeCents = this.querySelector('[data-mn-custom-name-fee-cents]');
        this.nameError = this.querySelector('[data-mn-custom-name-error]');
        this.nameFeeHint = this.querySelector('[data-mn-custom-name-fee-hint]');
      }

      /**
       * Ensure every swatch has a real #RRGGBB and is painted.
       * Liquid often emits ColorDrop strings that CSS/normalizeHex reject → white empty circles + unselectable.
       */
      applySwatchPaint(btn, hex) {
        if (!(btn instanceof Element) || !hex) return;
        btn.setAttribute('data-mn-color-hex', hex);
        btn.style.setProperty('--mn-custom-swatch', hex);
        btn.style.setProperty('background-color', hex, 'important');
      }

      resolveSwatchHex(btn) {
        const candidates = [
          btn.getAttribute('data-mn-color-hex'),
          btn.getAttribute('data-mn-color-name'),
          btn.style.getPropertyValue('--mn-custom-swatch'),
          btn.getAttribute('aria-label'),
          btn.getAttribute('title'),
        ];
        for (const raw of candidates) {
          const hex = this.normalizeHex(raw);
          if (hex) return hex;
        }
        return '';
      }

      readGroupColorsJson(groupEl) {
        const el = groupEl?.querySelector('[data-mn-group-colors-json]');
        if (!el) return [];
        try {
          const parsed = JSON.parse(el.textContent.trim() || '[]');
          if (!Array.isArray(parsed)) return [];
          return parsed
            .map((item) => {
              if (typeof item === 'string') return { css: item, name: '' };
              if (!item || typeof item !== 'object') return null;
              return {
                css: item.css || item.hex || item.color || '',
                name: item.name || item.label || '',
              };
            })
            .filter(Boolean);
        } catch (e) {
          return [];
        }
      }

      createSwatchButton({ hex, groupIndex, feeCents, feeLabel, feeVariantId, label }) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'mn-product-custom-options__color-swatch';
        btn.setAttribute('role', 'listitem');
        btn.dataset.mnColorSwatch = '';
        btn.dataset.mnColorGroupIndex = String(groupIndex);
        btn.dataset.mnColorFeeCents = String(feeCents || 0);
        btn.dataset.mnColorFeeLabel = feeLabel || '';
        if (feeVariantId) btn.dataset.mnColorFeeVariantId = String(feeVariantId);
        if (label) btn.dataset.mnColorName = label;
        btn.setAttribute('aria-label', label || hex);
        btn.setAttribute('aria-pressed', 'false');
        btn.title = label || hex;
        this.applySwatchPaint(btn, hex);
        return btn;
      }

      /**
       * Rebuild/repair swatch rows from each group's own color data (never a shared palette).
       */
      hydrateColorGroupsFromConfig() {
        if (!this.colorPanel) return;

        let groups = this.config.colorGroups || [];
        if (!groups.some((g) => g.colors?.length)) {
          const raw = this.readRawColorGroups().map((g) => this.normalizeColorGroup(g));
          if (raw.some((g) => g.colors.length || g.title)) {
            groups = raw;
            this.config.colorGroups = raw;
          }
        }

        const existingGroups = [...this.colorPanel.querySelectorAll('[data-mn-color-group]')];

        // Sync fee cents from the visible Shopify-formatted fee label (source of truth).
        existingGroups.forEach((el, index) => {
          const hint = el.querySelector('[data-mn-group-fee-hint], .mn-product-custom-options__fee-hint');
          const label = (hint?.textContent || el.getAttribute('data-mn-color-fee-label') || '')
            .replace(/[()+\s]/g, '')
            .trim();
          if (label) {
            const cents = this.parseMoneyLabelToCents(label);
            if (cents > 0) {
              el.setAttribute('data-mn-color-fee-cents', String(cents));
              el.setAttribute(
                'data-mn-color-fee-label',
                label.startsWith('$') || label.startsWith('€') ? label : this.formatMoney(cents)
              );
              if (groups[index]) {
                groups[index].priceCents = cents;
                groups[index].feeLabel =
                  el.getAttribute('data-mn-color-fee-label') || groups[index].feeLabel;
              }
            }
          }
        });

        // Rebuild swatches per group from that group's own JSON / config / DOM names.
        existingGroups.forEach((el, index) => {
          const row = el.querySelector('[data-mn-color-swatches]');
          if (!row) return;

          const feeCents = Number(el.getAttribute('data-mn-color-fee-cents')) || 0;
          const feeLabel = el.getAttribute('data-mn-color-fee-label') || '';
          const feeVariantId =
            Number(el.getAttribute('data-mn-color-fee-variant-id')) ||
            Number(groups[index]?.feeVariantId) ||
            0;
          const groupIndex = el.getAttribute('data-mn-color-group-index') || String(index);

          const fromGroupJson = this.readGroupColorsJson(el);
          const fromConfig = (groups[index]?.colors || []).map((c) => ({
            css: c,
            name: '',
          }));

          const sources = fromGroupJson.length ? fromGroupJson : fromConfig;
          const resolved = [];
          sources.forEach((item) => {
            const hex =
              this.normalizeHex(item.css) ||
              this.normalizeHex(item.name) ||
              this.normalizeHex(this.colorValueToCss(item.css));
            if (!hex) return;
            resolved.push({
              hex,
              name: item.name || '',
            });
          });

          // Repair existing Liquid swatches in place when we still have no source list.
          if (!resolved.length) {
            const existing = [...row.querySelectorAll('[data-mn-color-swatch]')];
            existing.forEach((btn) => {
              const hex = this.resolveSwatchHex(btn);
              if (hex) this.applySwatchPaint(btn, hex);
            });
            return;
          }

          row.innerHTML = '';
          resolved.forEach((item) => {
            row.appendChild(
              this.createSwatchButton({
                hex: item.hex,
                groupIndex,
                feeCents,
                feeLabel,
                feeVariantId,
                label: item.name || item.hex,
              })
            );
          });

          // Keep group JSON colors synced into config for ATC/debug.
          if (groups[index]) {
            groups[index].colors = resolved.map((r) => r.hex);
          }
        });

        // If Liquid rendered no groups at all, build from config as a last resort.
        if (!existingGroups.length && groups.length) {
          groups.forEach((group, groupIndex) => {
            const wrap = document.createElement('div');
            wrap.className = 'mn-product-custom-options__color-group';
            wrap.dataset.mnColorGroup = '';
            wrap.dataset.mnColorGroupIndex = String(groupIndex);
            wrap.dataset.mnColorFeeCents = String(group.priceCents || 0);
            wrap.dataset.mnColorFeeLabel = group.feeLabel || '';
            if (group.feeVariantId) {
              wrap.dataset.mnColorFeeVariantId = String(group.feeVariantId);
            }
            if (group.wallStripe === 'odd' || group.wallStripe === 'even') {
              wrap.dataset.mnWallStripe = group.wallStripe;
            }

            const head = document.createElement('div');
            head.className = 'mn-product-custom-options__color-group-head';
            const title = document.createElement('span');
            title.className = 'mn-product-custom-options__color-group-title';
            title.textContent = group.title || `Color Group ${groupIndex + 1}`;
            head.appendChild(title);
            if (group.feeLabel || group.priceCents > 0) {
              const fee = document.createElement('span');
              fee.className = 'mn-product-custom-options__fee-hint';
              fee.dataset.mnGroupFeeHint = '';
              fee.textContent = `(+${group.feeLabel || this.formatMoney(group.priceCents)})`;
              head.appendChild(fee);
            }
            wrap.appendChild(head);

            const row = document.createElement('div');
            row.className = 'mn-product-custom-options__color-swatches';
            row.setAttribute('role', 'list');
            row.dataset.mnColorSwatches = '';
            (group.colors || []).forEach((rawColor) => {
              const hex = this.normalizeHex(rawColor);
              if (!hex) return;
              row.appendChild(
                this.createSwatchButton({
                  hex,
                  groupIndex,
                  feeCents: group.priceCents || 0,
                  feeLabel: group.feeLabel || '',
                  feeVariantId: Number(group.feeVariantId) || 0,
                  label: `${group.title || 'Color'} ${hex}`,
                })
              );
            });
            wrap.appendChild(row);
            this.colorPanel.appendChild(wrap);
          });
        }

        // Final paint: only real resolved colors (no shared fake palette).
        this.colorPanel.querySelectorAll('[data-mn-color-swatch]').forEach((btn) => {
          const hex = this.resolveSwatchHex(btn);
          if (hex) this.applySwatchPaint(btn, hex);
        });

        this.config.colorGroups = groups;
        this.refreshElements();
        this.refreshGroupSelectedUi();
      }

      bindColorFieldset() {
        this.unbindColorFieldset();
        this.colorFieldset =
          this.productInfo?.querySelector('fieldset.mn-product-option--color, .mn-product-option--color') ||
          document.querySelector('fieldset.mn-product-option--color, .mn-product-option--color');
        this.colorFieldset?.addEventListener('click', this.onDefaultColorClick);
      }

      unbindColorFieldset() {
        this.colorFieldset?.removeEventListener('click', this.onDefaultColorClick);
      }

      onHostChange(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        this.refreshElements();

        if (target.matches('[data-mn-custom-color-toggle]')) {
          this.onColorToggle();
          return;
        }
        if (target.matches('[data-mn-custom-name-toggle]')) {
          this.onNameToggle();
          return;
        }
        if (target.matches('[data-mn-custom-name-input]')) {
          this.onNameInput();
        }
      }

      onHostClick(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const swatch = target.closest('[data-mn-color-swatch]');
        if (swatch && this.contains(swatch)) {
          event.preventDefault();
          this.refreshElements();
          this.onColorSwatchClick(swatch);
        }
      }

      readConfig() {
        const el = this.querySelector('[data-mn-custom-options-config]');
        let config = {};
        if (!el) {
          config = {
            allowCustomColor: this.dataset.allowCustomColor === 'true',
            allowCustomName: this.dataset.allowCustomName === 'true',
            customNamePrice: Number(this.dataset.customNamePrice) || 0,
            customNameFeeVariantId: Number(this.dataset.customNameFeeVariantId) || 0,
            colorGroups: [],
            colorFeeVariants: [],
            nameFeeVariants: [],
          };
        } else {
          try {
            config = JSON.parse(el.textContent) || {};
          } catch (e) {
            console.error('[mn-custom-options] config JSON', e);
            config = {
              allowCustomColor: this.dataset.allowCustomColor === 'true',
              allowCustomName: this.dataset.allowCustomName === 'true',
            };
          }
        }

        config.customNamePrice = Number(config.customNamePrice) || Number(this.dataset.customNamePrice) || 0;
        config.customNameFeeVariantId =
          Number(config.customNameFeeVariantId) || Number(this.dataset.customNameFeeVariantId) || 0;
        config.colorGroups = Array.isArray(config.colorGroups) ? config.colorGroups : [];
        config.colorGroups = config.colorGroups.map((group) => this.normalizeColorGroup(group));

        const rawGroups = this.readRawColorGroups().map((g) => this.normalizeColorGroup(g));
        if (rawGroups.length) {
          const configHasColors = config.colorGroups.some((g) => g.colors.length);
          if (!configHasColors) {
            config.colorGroups = rawGroups;
          } else {
            config.colorGroups = config.colorGroups.map((group, i) =>
              group.colors.length ? group : rawGroups[i] || group
            );
          }
        }

        const stripUsd = (label) =>
          String(label || '')
            .replace(/\s*USD\s*/gi, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        if (config.customNameFeeLabel) config.customNameFeeLabel = stripUsd(config.customNameFeeLabel);
        config.colorGroups.forEach((group) => {
          if (group.feeLabel) group.feeLabel = stripUsd(group.feeLabel);
          if (!group.priceCents && group.feeLabel) {
            group.priceCents = this.parseMoneyLabelToCents(group.feeLabel);
          }
          if (!group.feeLabel && group.priceCents > 0) {
            group.feeLabel = this.formatMoney(group.priceCents);
          }
        });

        if (!config.customNamePrice && config.customNameFeeLabel) {
          config.customNamePrice = this.parseMoneyLabelToCents(config.customNameFeeLabel);
        }

        const hasColorData = config.colorGroups.some((g) => g.colors.length > 0 || g.title);
        config.allowCustomColor =
          this.dataset.allowCustomColor === 'true' || Boolean(config.allowCustomColor) || hasColorData;

        config.samplePurchase =
          this.dataset.samplePurchase === 'true' || Boolean(config.samplePurchase);
        config.basePrice =
          Number(config.basePrice) || Number(this.dataset.basePrice) || 0;

        return config;
      }

      readRawColorGroups() {
        const el = this.querySelector('[data-mn-color-groups-raw]');
        if (!el) return [];
        try {
          const parsed = JSON.parse(el.textContent.trim() || '[]');
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          console.error('[mn-custom-options] raw color groups JSON', e);
          return [];
        }
      }

      normalizeColorGroup(group) {
        if (!group || typeof group !== 'object') {
          return {
            title: '',
            priceCents: 0,
            feeLabel: '',
            feeVariantId: 0,
            colors: [],
            wallStripe: null,
          };
        }

        const title =
          group.frontend_label ||
          group.frontendLabel ||
          group.title ||
          group.display_name ||
          group.name ||
          group.handle ||
          '';

        let feeLabel = String(group.feeLabel || '')
          .replace(/\s*USD\s*/gi, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        let priceCents = Number(group.priceCents) || 0;
        const feeVariantId =
          Number(group.feeVariantId) ||
          Number(group.fee_variant_id) ||
          Number(group.feeVariant?.id) ||
          0;

        // Prefer parsing the already-formatted fee label (avoids amount×100 bugs).
        if (feeLabel) {
          const fromLabel = this.parseMoneyLabelToCents(feeLabel);
          if (fromLabel > 0) priceCents = fromLabel;
        }

        const price = group.price;
        if (!priceCents && price != null) {
          if (typeof price === 'object') {
            // If JSON money looks like {amount:"2.0"}, treat as dollars.
            // If amount is a large integer with no decimal, treat as cents.
            if (price.amount != null && price.amount !== '') {
              const amountStr = String(price.amount);
              const amount = parseFloat(amountStr);
              if (Number.isFinite(amount)) {
                priceCents = amountStr.includes('.')
                  ? Math.round(amount * 100)
                  : amount >= 100
                    ? Math.round(amount)
                    : Math.round(amount * 100);
              }
            } else if (price.cents != null) {
              priceCents = Number(price.cents) || 0;
            }
          } else if (typeof price === 'number' || typeof price === 'string') {
            const amountStr = String(price);
            const amount = parseFloat(amountStr);
            if (Number.isFinite(amount)) {
              priceCents = amountStr.includes('.')
                ? Math.round(amount * 100)
                : amount >= 100
                  ? Math.round(amount)
                  : Math.round(amount * 100);
            }
          }
        }
        if (!feeLabel && priceCents > 0) {
          try {
            feeLabel = this.formatMoney(priceCents);
          } catch (e) {
            feeLabel = `$${(priceCents / 100).toFixed(2)}`;
          }
        }

        const rawColors = group.colors?.value || group.colors || group.colorNames || [];
        const colors = (Array.isArray(rawColors) ? rawColors : [])
          .map((c) => this.colorValueToCss(c))
          .map((c) => this.normalizeHex(c))
          .filter(Boolean);

        return {
          title,
          priceCents,
          feeLabel,
          feeVariantId,
          colors,
          wallStripe: group.wallStripe || null,
        };
      }

      colorValueToCss(color) {
        if (color == null) return '';
        if (typeof color === 'string') {
          const trimmed = color.trim();
          if (!trimmed) return '';
          if (trimmed.includes('#') || /rgb/i.test(trimmed)) return trimmed;
          return this.colorNameToHex(trimmed) || trimmed;
        }
        if (typeof color !== 'object') return String(color);
        if (color.css) return this.colorValueToCss(color.css);
        if (color.hex) return String(color.hex);
        if (color.label || color.name || color.title) {
          const nested =
            color.color?.value || color.color || color.hex?.value || color.hex || color.colour;
          if (nested != null && nested !== color) {
            const fromNested = this.colorValueToCss(nested);
            if (fromNested) return fromNested;
          }
          const label = String(color.label || color.name || color.title || '');
          return this.colorNameToHex(label) || '';
        }
        if (color.red != null || color.green != null || color.blue != null) {
          return `rgb(${Number(color.red) || 0}, ${Number(color.green) || 0}, ${Number(color.blue) || 0})`;
        }
        if (color.rgb != null && color.rgb !== '') {
          const rgb = String(color.rgb).trim();
          if (/rgb/i.test(rgb)) return rgb;
          const parts = rgb.split(/[\s,]+/).filter(Boolean);
          if (parts.length >= 3) {
            return `rgb(${parts[0]}, ${parts[1]}, ${parts[2]})`;
          }
        }
        if (color.value != null) return this.colorValueToCss(color.value);
        return '';
      }

      colorNameToHex(name) {
        const key = String(name || '')
          .trim()
          .toLowerCase();
        const map = {
          red: '#dc2626',
          rouge: '#dc2626',
          brown: '#a16207',
          tan: '#a16207',
          beige: '#a16207',
          taupe: '#a16207',
          blue: '#2563eb',
          bleu: '#2563eb',
          'light blue': '#7dd3fc',
          'sky blue': '#7dd3fc',
          sky: '#7dd3fc',
          'baby blue': '#7dd3fc',
          navy: '#1e3a8a',
          'navy blue': '#1e3a8a',
          'dark blue': '#1e3a8a',
          pink: '#ec4899',
          rose: '#ec4899',
          orange: '#f97316',
          coral: '#f97316',
          peach: '#fab683',
          yellow: '#eab308',
          gold: '#eab308',
          green: '#16a34a',
          vert: '#16a34a',
          teal: '#14b8a6',
          turquoise: '#14b8a6',
          purple: '#8b5cf6',
          violet: '#8b5cf6',
          lavender: '#8b5cf6',
          black: '#1a1a1a',
          noir: '#1a1a1a',
          white: '#f5f5f5',
          blanc: '#f5f5f5',
          ivory: '#f5f5f5',
          cream: '#f5f5f5',
          grey: '#9ca3af',
          gray: '#9ca3af',
          silver: '#9ca3af',
          charcoal: '#4b5563',
          burgundy: '#7f1d1d',
          maroon: '#7f1d1d',
        };
        return map[key] || '';
      }

      parseMoneyLabelToCents(label) {
        if (!label) return 0;
        const match = String(label).replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
        if (!match) return 0;
        return Math.round(parseFloat(match[1]) * 100);
      }

      readVariantPriceCents(variant) {
        if (variant && variant.price != null) {
          const n = Number(variant.price);
          if (Number.isFinite(n) && n > 0) return n;
        }
        const variantScript =
          this.productInfo?.querySelector('variant-selects [data-selected-variant]') ||
          document.querySelector(`#variant-selects-${this.sectionId} [data-selected-variant]`);
        if (variantScript) {
          try {
            const parsed = JSON.parse(variantScript.textContent);
            const n = Number(parsed?.price);
            if (Number.isFinite(n) && n > 0) return n;
          } catch (e) {
            /* ignore */
          }
        }
        const fallback = Number(this.config?.basePrice) || Number(this.dataset.basePrice) || 0;
        return Number.isFinite(fallback) && fallback > 0 ? fallback : 0;
      }

      readBasePriceFromDom() {
        return this.readVariantPriceCents();
      }

      syncFeeHints() {
        const clean = (label) =>
          String(label || '')
            .replace(/\s*USD\s*/gi, ' ')
            .replace(/\s+/g, ' ')
            .trim();

        const applyHint = (el, label) => {
          if (!el) return;
          const text = clean(label);
          if (!text) {
            el.setAttribute('hidden', '');
            el.textContent = '';
            return;
          }
          el.textContent = `(+${text})`;
          el.removeAttribute('hidden');
        };

        applyHint(
          this.nameFeeHint,
          this.config.customNameFeeLabel ||
            (Number(this.config.customNamePrice) > 0 ? this.formatMoney(this.config.customNamePrice) : '')
        );

        const colorLabel =
          this.getSelectedColorFeeLabel() ||
          (this.getSelectedColorFeeCents() > 0 ? this.formatMoney(this.getSelectedColorFeeCents()) : '');
        applyHint(this.colorFeeHint, this.isCustomColorActive() ? colorLabel : '');
      }

      isCustomColorActive() {
        if (!this.colorToggle?.isConnected) {
          this.colorToggle = this.querySelector('[data-mn-custom-color-toggle]');
        }
        // If the toggle is in the DOM, trust its checked state (don't block on config).
        return Boolean(this.colorToggle?.checked);
      }

      isCustomNameActive() {
        if (!this.nameToggle?.isConnected) {
          this.nameToggle = this.querySelector('[data-mn-custom-name-toggle]');
        }
        return Boolean(this.nameToggle?.checked);
      }

      normalizeHex(value) {
        if (value == null) return '';
        if (typeof value === 'object') {
          return this.normalizeHex(this.colorValueToCss(value));
        }

        let raw = String(value).trim();
        if (!raw) return '';

        // Strip accidental wrapping quotes from Liquid/JSON.
        raw = raw.replace(/^['"]+|['"]+$/g, '').trim();

        const fromName = this.colorNameToHex(raw);
        if (fromName && !raw.includes('#') && !/rgb/i.test(raw) && !/^[\dA-Fa-f]{3,8}$/.test(raw)) {
          return fromName.toUpperCase();
        }

        // rgb(R, G, B) / rgb(R G B) / rgba(...) with optional decimals
        const rgbMatch = raw.match(
          /^rgba?\(\s*(\d+(?:\.\d+)?)\s*[, ]\s*(\d+(?:\.\d+)?)\s*[, ]\s*(\d+(?:\.\d+)?)/i
        );
        if (rgbMatch) {
          const toHex = (n) =>
            Math.max(0, Math.min(255, Math.round(parseFloat(n))))
              .toString(16)
              .padStart(2, '0');
          return `#${toHex(rgbMatch[1])}${toHex(rgbMatch[2])}${toHex(rgbMatch[3])}`.toUpperCase();
        }

        const bareRgb = raw.match(/^(\d{1,3}(?:\.\d+)?)\s+(\d{1,3}(?:\.\d+)?)\s+(\d{1,3}(?:\.\d+)?)$/);
        if (bareRgb) {
          const toHex = (n) =>
            Math.max(0, Math.min(255, Math.round(parseFloat(n))))
              .toString(16)
              .padStart(2, '0');
          return `#${toHex(bareRgb[1])}${toHex(bareRgb[2])}${toHex(bareRgb[3])}`.toUpperCase();
        }

        let hex = raw;
        if (hex[0] !== '#') hex = `#${hex}`;
        if (/^#[0-9A-Fa-f]{6}$/.test(hex)) return hex.toUpperCase();
        if (/^#[0-9A-Fa-f]{8}$/.test(hex)) return hex.slice(0, 7).toUpperCase();
        if (/^#[0-9A-Fa-f]{3}$/.test(hex)) {
          const r = hex[1];
          const g = hex[2];
          const b = hex[3];
          return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
        }
        return fromName ? fromName.toUpperCase() : '';
      }

      getColorSelections() {
        return [...(this.colorSelections?.values() || [])];
      }

      getSelectedHex() {
        const hexes = this.getColorSelections()
          .map((s) => this.normalizeHex(s.hex))
          .filter(Boolean);
        return hexes.join(', ');
      }

      getSelectedColorFeeCents() {
        return this.getColorSelections().reduce((sum, s) => sum + (Number(s.feeCents) || 0), 0);
      }

      getSelectedColorFeeLabel() {
        const cents = this.getSelectedColorFeeCents();
        return cents > 0 ? this.formatMoney(cents) : '';
      }

      getSelectedColorPropertyValue() {
        // Format per selection: GroupTitle :: ColorName :: #HEX :: feeLabel
        // Multiple selections joined by " | " for readability in Shopify admin
        const parts = this.getColorSelections()
          .map((s) => {
            const hex = this.normalizeHex(s.hex);
            if (!hex) return '';
            const groupTitle = String(s.title || 'Color')
              .trim()
              .replace(/::/g, ' ')
              .trim();
            const colorName = String(s.name || '')
              .trim()
              .replace(/::/g, ' ')
              .trim();
            const fee =
              String(s.feeLabel || '')
                .replace(/::/g, ' ')
                .trim() || (Number(s.feeCents) > 0 ? this.formatMoney(s.feeCents) : '');
            const label = colorName ? `${colorName} (${groupTitle})` : groupTitle;
            return fee ? `${label} [${hex}] +${fee}` : `${label} [${hex}]`;
          })
          .filter(Boolean);
        return parts.join(', ');
      }

      getSelectedColorPropertyValueStructured() {
        // Machine-readable: GroupTitle|#HEX|ColorName|fee  (entries separated by @@)
        return this.getColorSelections()
          .map((s) => {
            const hex = this.normalizeHex(s.hex);
            if (!hex) return '';
            const groupTitle = String(s.title || 'Color').trim().replace(/[|@]/g, ' ').trim();
            const colorName = String(s.name || '').trim().replace(/[|@]/g, ' ').trim();
            const fee =
              String(s.feeLabel || '').replace(/[|@]/g, ' ').trim() ||
              (Number(s.feeCents) > 0 ? this.formatMoney(s.feeCents) : '');
            return `${groupTitle}|${hex}|${colorName}|${fee}`;
          })
          .filter(Boolean)
          .join('@@');
      }

      clearSwatchSelectionUi() {
        this.querySelectorAll('[data-mn-color-swatch]').forEach((btn) => {
          btn.classList.remove('is-selected');
          btn.setAttribute('aria-pressed', 'false');
        });
        this.querySelectorAll('[data-mn-color-group]').forEach((el) => {
          el.classList.remove('is-selected');
        });
      }

      refreshGroupSelectedUi() {
        this.querySelectorAll('[data-mn-color-group]').forEach((el) => {
          const key = el.getAttribute('data-mn-color-group-index') || '';
          const selected = this.colorSelections?.get(key);
          el.classList.toggle('is-selected', Boolean(selected));
          el.querySelectorAll('[data-mn-color-swatch]').forEach((btn) => {
            const hex = this.normalizeHex(btn.getAttribute('data-mn-color-hex'));
            const on = Boolean(selected && this.normalizeHex(selected.hex) === hex);
            btn.classList.toggle('is-selected', on);
            btn.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
        });
      }

      setColorFieldsetMode(customOn) {
        if (!this.colorFieldset) return;
        this.colorFieldset.classList.toggle('mn-custom-color-active', customOn);
        this.colorFieldset.querySelectorAll('input.swatch-input__input, input[type="radio"]').forEach((input) => {
          input.tabIndex = customOn ? -1 : 0;
          if (customOn) {
            input.setAttribute('data-mn-was-checked', input.checked ? '1' : '0');
          }
        });
      }

      clearCatalogColorSelectionVisual() {
        if (!this.colorFieldset) return;
        this.colorFieldset.classList.add('mn-custom-color-active');
        this.colorFieldset.querySelectorAll('.swatch-input__label, label').forEach((label) => {
          label.classList.remove('is-selected');
        });
      }

      restoreCatalogColorInteraction() {
        if (!this.colorFieldset) return;
        this.colorFieldset.classList.remove('mn-custom-color-active');
        this.colorFieldset.querySelectorAll('input.swatch-input__input, input[type="radio"]').forEach((input) => {
          input.tabIndex = 0;
        });
      }

      syncColorProperties() {
        const on = this.isCustomColorActive();
        const colorValue = this.getSelectedColorPropertyValue();
        const colorDataValue = on ? this.getSelectedColorPropertyValueStructured() : '';
        const feeCents = this.getSelectedColorFeeCents();
        const feeLabel = this.getSelectedColorFeeLabel();

        if (this.colorProperty) {
          this.colorProperty.disabled = !(on && colorValue);
          this.colorProperty.value = on && colorValue ? colorValue : '';
        }
        if (this.colorDataProperty) {
          const dataOn = on && colorDataValue;
          this.colorDataProperty.disabled = !dataOn;
          this.colorDataProperty.value = dataOn ? colorDataValue : '';
        }
        const colorFeeOn = on && colorValue && feeCents > 0;
        if (this.colorFeeProperty) {
          this.colorFeeProperty.disabled = !colorFeeOn;
          if (colorFeeOn) this.colorFeeProperty.value = feeLabel;
        }
        if (this.colorFeeCents) {
          this.colorFeeCents.disabled = !colorFeeOn;
          if (colorFeeOn) this.colorFeeCents.value = String(feeCents);
        }
      }

      syncNameFeeProperty() {
        const on = this.isCustomNameActive() && Number(this.config.customNamePrice) > 0;
        if (this.nameFeeProperty) {
          this.nameFeeProperty.disabled = !on;
          if (on) this.nameFeeProperty.value = this.config.customNameFeeLabel || '';
        }
        if (this.nameFeeCents) {
          this.nameFeeCents.disabled = !on;
          if (on) this.nameFeeCents.value = String(Number(this.config.customNamePrice) || 0);
        }
      }

      syncFromSelection() {
        this.refreshElements();
        const colorOn = this.isCustomColorActive();
        if (this.colorPanel) {
          if (colorOn) {
            this.colorPanel.hidden = false;
            this.colorPanel.removeAttribute('hidden');
          } else {
            this.colorPanel.hidden = true;
            this.colorPanel.setAttribute('hidden', '');
          }
        }

        if (colorOn) {
          this.setColorFieldsetMode(true);
          this.clearCatalogColorSelectionVisual();
          this.syncColorProperties();
        } else {
          this.restoreCatalogColorInteraction();
          this.colorSelections = new Map();
          this.clearSwatchSelectionUi();
          if (this.colorProperty) {
            this.colorProperty.disabled = true;
            this.colorProperty.value = '';
          }
          if (this.colorDataProperty) {
            this.colorDataProperty.disabled = true;
            this.colorDataProperty.value = '';
          }
          if (this.colorFeeProperty) this.colorFeeProperty.disabled = true;
          if (this.colorFeeCents) this.colorFeeCents.disabled = true;
        }

        if (this.nameInput && this.nameToggle) {
          const on = this.nameToggle.checked;
          this.nameInput.hidden = !on;
          this.nameInput.disabled = !on;
          this.nameInput.required = on;
          if (!on) {
            this.nameInput.value = '';
            this.nameInput.removeAttribute('aria-invalid');
          }
        }
        this.syncNameFeeProperty();
        this.syncFeeHints();
        try {
          this.publishWallPreviewColors();
        } catch (e) {
          console.error('[mn-custom-options] wall preview publish', e);
        }
      }

      /**
       * Wall stripes: ONLY Color element stripe odd/even (data-mn-wall-stripe).
       * color_element_group_list selections never paint the wall.
       */
      getWallPreviewColors() {
        const defaults = this.getWallDefaultColors();
        const wallOn = Boolean(this.config?.wallPreview) || this.dataset.wallPreview === 'true';

        if (!wallOn) {
          return { c1: defaults.c1, c2: defaults.c2, active: false };
        }

        if (!this.isCustomColorActive()) {
          return { c1: defaults.c1, c2: defaults.c2, active: false };
        }

        const oddGroup = this.querySelector('[data-mn-wall-stripe="odd"]');
        const evenGroup = this.querySelector('[data-mn-wall-stripe="even"]');
        if (!oddGroup && !evenGroup) {
          return { c1: defaults.c1, c2: defaults.c2, active: false };
        }

        const oddIdx = oddGroup?.getAttribute('data-mn-color-group-index');
        const evenIdx = evenGroup?.getAttribute('data-mn-color-group-index');
        const s0 = oddIdx != null ? this.colorSelections.get(String(oddIdx)) : null;
        const s1 = evenIdx != null ? this.colorSelections.get(String(evenIdx)) : null;
        const hasWallPick = Boolean(s0?.hex || s1?.hex);

        return {
          c1: this.normalizeHex(s0?.hex) || defaults.c1,
          c2: this.normalizeHex(s1?.hex) || defaults.c2,
          active: hasWallPick,
        };
      }

      getWallDefaultColors() {
        const fromConfigC1 = this.config?.wallDefaultC1 || this.dataset.wallDefaultC1;
        const fromConfigC2 = this.config?.wallDefaultC2 || this.dataset.wallDefaultC2;
        const wallEl = document.querySelector('mn-wall-preview');
        const cssC1 = wallEl ? getComputedStyle(wallEl).getPropertyValue('--wall-c1').trim() : '';
        const cssC2 = wallEl ? getComputedStyle(wallEl).getPropertyValue('--wall-c2').trim() : '';
        return {
          c1:
            this.normalizeHex(fromConfigC1) ||
            this.normalizeHex(wallEl?.dataset?.defaultC1) ||
            this.normalizeHex(cssC1) ||
            '#FFFFFF',
          c2:
            this.normalizeHex(fromConfigC2) ||
            this.normalizeHex(wallEl?.dataset?.defaultC2) ||
            this.normalizeHex(cssC2) ||
            '#FFFFFF',
        };
      }

      publishWallPreviewColors() {
        const detail = this.getWallPreviewColors();
        document.dispatchEvent(
          new CustomEvent('mn:wall-preview-colors', {
            detail,
            bubbles: true,
          })
        );
      }

      onColorToggle() {
        this.refreshElements();
        this.clearError();
        try {
          if (!this.isCustomColorActive()) {
            this.colorSelections = new Map();
            this.clearSwatchSelectionUi();
          } else {
            this.hydrateColorGroupsFromConfig();
            this.refreshGroupSelectedUi();
          }
        } catch (e) {
          console.error('[mn-custom-options] onColorToggle', e);
        }
        this.syncFromSelection();
        this.updatePriceDisplay();
      }

      onColorSwatchClick(swatch) {
        this.clearError();
        if (!this.colorToggle?.checked) {
          this.colorToggle.checked = true;
        }

        const hex = this.resolveSwatchHex(swatch);
        if (!hex) return;

        this.applySwatchPaint(swatch, hex);

        const group = swatch.closest('[data-mn-color-group]');
        const groupIndex = group?.getAttribute('data-mn-color-group-index') || '0';
        const current = this.colorSelections.get(groupIndex);
        const isSame = current && this.normalizeHex(current.hex) === hex;

        if (isSame) {
          this.colorSelections.delete(groupIndex);
        } else {
          const title =
            group?.querySelector('.mn-product-custom-options__color-group-title')?.textContent?.trim() ||
            '';
          this.colorSelections.set(groupIndex, {
            hex,
            feeCents: Number(swatch.getAttribute('data-mn-color-fee-cents')) || 0,
            feeLabel: (swatch.getAttribute('data-mn-color-fee-label') || '').trim(),
            feeVariantId:
              Number(swatch.getAttribute('data-mn-color-fee-variant-id')) ||
              Number(group?.getAttribute('data-mn-color-fee-variant-id')) ||
              0,
            title,
            name: (swatch.getAttribute('data-mn-color-name') || '').trim(),
          });
        }

        this.refreshGroupSelectedUi();
        this.syncFromSelection();
        this.updatePriceDisplay();
      }

      onDefaultColorClick(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;
        this.refreshElements();
        const radio = target.closest(
          'input.swatch-input__input, label.swatch-input__label, input[type="radio"]'
        );
        if (!radio) return;
        if (this.colorToggle?.checked) {
          this.colorToggle.checked = false;
          this.syncFromSelection();
          this.updatePriceDisplay();
        }
      }

      onNameToggle() {
        this.refreshElements();
        this.clearError();
        this.syncFromSelection();
        this.updatePriceDisplay();
      }

      onNameInput() {
        this.refreshElements();
        this.clearError();
        if (this.nameInput) this.nameInput.removeAttribute('aria-invalid');
        this.syncNameFeeProperty();
        this.updatePriceDisplay();
      }

      onOptionValueChange({ data }) {
        if (data?.event && this.productInfo && !this.productInfo.contains(data.event.target)) return;

        this.refreshElements();
        this.bindColorFieldset();

        const colorChanged = data?.event?.target?.closest?.(
          '.mn-product-option--color, fieldset.mn-product-option--color'
        );
        if (colorChanged && this.colorToggle?.checked) {
          this.colorToggle.checked = false;
          this.syncFromSelection();
        }
      }

      onVariantUpdated({ data } = {}) {
        this.refreshElements();
        this.bindColorFieldset();

        if (this.isSamplePurchase()) {
          this.variantPriceCents =
            Number(this.config?.basePrice) || Number(this.dataset.basePrice) || this.variantPriceCents;
          this.updatePriceDisplay();
          return;
        }

        const price = this.readVariantPriceCents(data?.variant);
        if (price > 0) this.variantPriceCents = price;

        this.updatePriceDisplay();
        if (this.priceUpdateTimer) clearTimeout(this.priceUpdateTimer);
        this.priceUpdateTimer = setTimeout(() => {
          this.priceUpdateTimer = null;
          this.refreshElements();
          const again = this.readVariantPriceCents(data?.variant);
          if (again > 0) this.variantPriceCents = again;
          this.updatePriceDisplay();
        }, 120);
      }

      isSamplePurchase() {
        return this.dataset.samplePurchase === 'true' || this.config?.samplePurchase === true;
      }

      getFeeCents() {
        if (this.isSamplePurchase()) return 0;
        let extra = 0;
        if (this.isCustomColorActive() && this.getColorSelections().length) {
          extra += this.getSelectedColorFeeCents();
        }
        if (this.isCustomNameActive()) {
          extra += Number(this.config.customNamePrice) || 0;
        }
        return extra;
      }

      formatMoney(cents) {
        const amount = (Number(cents) || 0) / 100;
        const code =
          this.config?.currencyCode || this.dataset.currencyCode || 'USD';
        try {
          return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: code,
            currencyDisplay: 'narrowSymbol',
          }).format(amount);
        } catch (e) {
          return `$${amount.toFixed(2)}`;
        }
      }

      getPrimaryPriceNode(priceRoot) {
        const priceEl = priceRoot.classList.contains('price')
          ? priceRoot
          : priceRoot.querySelector('.price') || priceRoot;
        const onSale = priceEl.classList.contains('price--on-sale');

        if (onSale) {
          return (
            priceRoot.querySelector('.price__sale .price-item--sale:not(s)') ||
            priceRoot.querySelector('.price-item--sale:not(s)')
          );
        }

        return (
          priceRoot.querySelector('.price__regular .price-item--regular:not(s)') ||
          priceRoot.querySelector('.price-item--regular:not(s)')
        );
      }

      updatePriceDisplay() {
        if (
          !this.isSamplePurchase() &&
          document.querySelector('mn-wallpaper-order[data-mn-is-wallpaper="true"]')
        ) {
          const wallpaperOrder =
            this.closest('product-info')?.querySelector('mn-wallpaper-order') ||
            document.querySelector('mn-wallpaper-order');
          wallpaperOrder?.update?.();
          return;
        }

        if (this.isSamplePurchase()) {
          const sampleBase =
            Number(this.config?.basePrice) || Number(this.dataset.basePrice) || this.variantPriceCents;
          if (sampleBase > 0) this.variantPriceCents = sampleBase;
        } else {
          const fresh = this.readVariantPriceCents();
          if (fresh > 0) this.variantPriceCents = fresh;
        }

        const base = Number(this.variantPriceCents) || 0;
        const fees = this.getFeeCents();
        const qty = this.isSamplePurchase() ? this.getSelectedQuantity() : 1;
        const total = (base + fees) * qty;

        const priceRoot =
          document.getElementById(`price-${this.sectionId}`) ||
          this.productInfo?.querySelector(`#price-${this.sectionId}`) ||
          this.productInfo?.querySelector('[id^="price-"]');
        if (!priceRoot) return;

        const target = this.getPrimaryPriceNode(priceRoot);
        if (!target) return;

        target.dataset.mnVariantPrice = String(base);
        target.dataset.mnFeeCents = String(fees);
        target.dataset.mnQty = String(qty);
        target.textContent = this.formatMoney(total);
      }

      resolveFeeLine(variants, priceCents, lineQuantity, preferredVariantId) {
        const qty = Math.max(1, parseInt(lineQuantity, 10) || 1);
        const preferred = Number(preferredVariantId) || 0;
        if (preferred > 0) {
          return { id: preferred, quantity: qty };
        }
        if (!Array.isArray(variants) || !variants.length) return null;
        const fee = Number(priceCents) || 0;
        if (fee <= 0) {
          return { id: variants[0].id, quantity: qty };
        }
        const candidates = [...new Set([fee, Math.round(fee * 100), Math.round(fee / 100)].filter((n) => n > 0))];

        for (const c of candidates) {
          const match = variants.find((v) => Number(v.price) === c);
          if (match) return { id: match.id, quantity: qty };
        }

        // No exact price match — do not silently use the wrong variant.
        console.warn('[mn-custom-options] No color fee variant matches price', fee, variants);
        return null;
      }

      async ensureColorFeeVariants() {
        if (Array.isArray(this.config?.colorFeeVariants) && this.config.colorFeeVariants.length) {
          return this.config.colorFeeVariants;
        }
        const handles = ['mn-custom-color-fee', 'custom-color-fee', 'mn-color-fee'];
        const root = window.Shopify?.routes?.root || '/';
        for (const handle of handles) {
          try {
            const product = await fetch(`${root}products/${handle}.js`).then((r) =>
              r.ok ? r.json() : null
            );
            if (product?.variants?.length) {
              this.config.colorFeeVariants = product.variants.map((v) => ({
                id: v.id,
                price: Number(v.price),
              }));
              return this.config.colorFeeVariants;
            }
          } catch (e) {
            /* try next */
          }
        }
        return [];
      }

      clearError() {
        [this.nameError, this.colorError].forEach((el) => {
          if (!el) return;
          el.setAttribute('hidden', '');
          const text = el.querySelector('.mn-product-custom-options__error-text');
          if (text) text.textContent = '';
        });
      }

      showError(message, field = 'name') {
        this.clearError();
        const target = field === 'color' ? this.colorError : this.nameError;
        if (!target) return;
        const text = target.querySelector('.mn-product-custom-options__error-text');
        if (text) text.textContent = message;
        target.removeAttribute('hidden');
        if (field === 'color' && this.colorToggle) {
          this.colorToggle.checked = true;
          this.syncFromSelection();
          requestAnimationFrame(() => {
            this.colorPanel?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            const first = this.querySelector('[data-mn-color-swatch]');
            first?.focus();
          });
        }
        if (field === 'name' && this.nameInput && this.nameToggle) {
          this.nameToggle.checked = true;
          this.nameInput.hidden = false;
          this.nameInput.disabled = false;
          this.nameInput.required = true;
          this.nameInput.setAttribute('aria-invalid', 'true');
          requestAnimationFrame(() => {
            this.nameInput.focus();
            this.nameInput.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          });
        }
      }

      validate() {
        this.refreshElements();
        if (this.isCustomColorActive()) {
          if (!this.getColorSelections().length) {
            return { message: 'Please pick a custom color.', field: 'color' };
          }
        }
        if (this.config.allowCustomName && this.nameToggle?.checked) {
          if (!this.nameInput?.value?.trim()) {
            return { message: 'Please enter a custom name.', field: 'name' };
          }
        }
        return null;
      }

      buildCartAddPayload(formData) {
        this.refreshElements();
        this.clearError();
        this.syncFromSelection();

        // Sample purchase: charge catalog sample SKU only (no fee lines). FormData handles add.
        if (this.isSamplePurchase()) return null;

        const nameChecked = Boolean(this.config.allowCustomName && this.nameToggle?.checked);
        const colorActive = this.isCustomColorActive();

        if (!nameChecked && !colorActive) return null;

        const error = this.validate();
        if (error) {
          this.showError(error.message, error.field);
          return { error: error.message, errorField: error.field };
        }

        const colorSelections = this.getColorSelections();
        const colorValue = this.getSelectedColorPropertyValue();
        const needsColor = colorActive && colorSelections.length > 0;
        const needsName = nameChecked && this.nameInput?.value?.trim();
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        const isWallpaper = Boolean(document.querySelector('mn-wallpaper-order[data-mn-is-wallpaper="true"]'));

        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value && !match[1].startsWith('_')) properties[match[1]] = value;
        }
        if (needsName) {
          properties['Custom name'] = this.nameInput.value.trim();
        }
        if (needsColor) {
          properties['Custom color'] = colorValue;
          properties['_custom_color_data'] = this.getSelectedColorPropertyValueStructured();
        }

        const colorFeeCents = this.getSelectedColorFeeCents();
        const colorFeeLabel = this.getSelectedColorFeeLabel();
        const nameFeeVariantId = Number(this.config.customNameFeeVariantId) || 0;
        // Name fee scales with product qty (e.g. $10 × 2 rolls = $20).
        const nameFee =
          needsName && nameFeeVariantId > 0
            ? { id: nameFeeVariantId, quantity }
            : null;

        if (needsName && (this.config.customNameFeeLabel || Number(this.config.customNamePrice) > 0)) {
          // Keep fee amount in underscore props only — Shopify checkout shows
          // non-underscore properties, and the fee SKU already charges the amount.
          properties['_custom_name_fee_cents'] = String(Number(this.config.customNamePrice) || 0);
          if (nameFee?.id) properties['_custom_name_fee_variant_id'] = String(nameFee.id);
        }
        if (needsColor && colorFeeLabel) {
          properties['Custom color fee'] = colorFeeLabel;
          properties['_custom_color_fee_cents'] = String(colorFeeCents || 0);
        }

        const parentVariantId = Number(formData.get('id'));
        const items = [
          {
            id: parentVariantId,
            quantity,
            properties,
          },
        ];

        if (needsColor) {
          const feeQtyByVariant = new Map();
          const idList = [];
          colorSelections.forEach((sel) => {
            // Prefer metaobject fee variant id; else match by price. Qty = rolls.
            const fee = this.resolveFeeLine(
              this.config.colorFeeVariants,
              Number(sel.feeCents) || 0,
              quantity,
              Number(sel.feeVariantId) || 0
            );
            if (!fee) return;
            idList.push(fee.id);
            feeQtyByVariant.set(fee.id, (feeQtyByVariant.get(fee.id) || 0) + fee.quantity);
          });
          if (idList.length) {
            properties['_custom_color_fee_variant_ids'] = idList.join(',');
          }
          feeQtyByVariant.forEach((qty, id) => {
            const feeLine = {
              id,
              quantity: qty,
              properties: {
                _fee_type: 'custom_color',
                _hide_in_cart: '1',
              },
            };
            if (parentVariantId > 0) feeLine.parent_id = parentVariantId;
            items.push(feeLine);
          });
        }

        if (nameFee) {
          const feeLine = {
            id: nameFee.id,
            quantity: nameFee.quantity,
            properties: {
              _fee_type: 'custom_name',
              _hide_in_cart: '1',
            },
          };
          if (parentVariantId > 0) feeLine.parent_id = parentVariantId;
          items.push(feeLine);
        }

        // Wallpaper: personalization fee lines + properties; design fee added in product-form.
        if (isWallpaper) {
          return { items };
        }

        return { items };
      }
    }
  );
}
